import { styled } from "@mui/material";
import { useMemo } from "react";
import type { TooltipData } from "../TooltipProvider";
import useTooltip from "../TooltipProvider/useTooltipStore";
import useAreaChart from "../AreaChartProvider/useAreaChartStore";

const TOOLTIP_OFFSET = 12;

const TooltipContainer = styled("div")({
  position: "absolute",
  // Without this the box is squeezed into the space left of `left`, shrinking it near the right edge.
  width: "max-content",
  // Breaks words too long to wrap, such as long names, once the side's max width is reached.
  overflowWrap: "anywhere",
  pointerEvents: "none",
  zIndex: 1,
});

export interface TooltipProps<T> {
  id?: string;
  renderItem?: (data: TooltipData<T>) => React.ReactNode;
}

function Tooltip<T>(props: TooltipProps<T>) {
  const { id, renderItem = () => null } = props;
  const { width } = useAreaChart();
  const { tooltipLeft = 0, tooltipTop = 0, tooltipData, tooltipOpen } = useTooltip();

  const tooltip = useMemo(() => {
    return {
      ...tooltipData,
      tooltipOpen,
    } as TooltipData<T>;
  }, [tooltipData, tooltipOpen]);

  // Opens towards the side with more room so it never covers the hovered point,
  // and is capped to that side's room so wide content wraps instead of leaving the chart.
  const placement = useMemo(() => {
    const opensLeft = tooltipLeft > width / 2;
    return {
      translateX: opensLeft ? `calc(-100% - ${TOOLTIP_OFFSET}px)` : `${TOOLTIP_OFFSET}px`,
      maxWidth: Math.max(0, (opensLeft ? tooltipLeft : width - tooltipLeft) - TOOLTIP_OFFSET),
    };
  }, [tooltipLeft, width]);

  if (!tooltipOpen) return null;

  return (
    <TooltipContainer
      id={id}
      role="tooltip"
      style={{
        left: tooltipLeft,
        top: tooltipTop,
        maxWidth: placement.maxWidth,
        transform: `translateX(${placement.translateX})`,
      }}
    >
      {renderItem(tooltip)}
    </TooltipContainer>
  );
}

export default Tooltip;
